/**
 * Core game logic orchestration
 * 
 * The GameEngine separates business logic from UI concerns, making the code
 * more testable, maintainable, and reusable. It coordinates the PDA, CFG,
 * and Graph to manage game state and enforce game rules.
 * 
 * Educational Purpose:
 * - Demonstrates separation of concerns architecture
 * - Shows how to coordinate multiple computational models
 * - Provides a clean API for UI components to interact with game logic
 * 
 * Design Pattern: Facade
 * The GameEngine acts as a facade, providing a simplified interface to
 * the complex subsystems (PDA, Graph, CFG).
 */

import { Graph } from './Graph';
import { PDA } from './PDA';
import { GameStatus } from './types';

export class GameEngine {
  private graph: Graph;
  private pda: PDA;
  private currentRoom: number;
  private visited: Set<number>;

  /**
   * Initialize a new game with specified number of rooms
   * 
   * @param numRooms - Number of rooms to generate (affects difficulty)
   */
  constructor(numRooms: number) {
    this.graph = new Graph(numRooms);
    this.pda = new PDA();
    this.currentRoom = 0; // Always start in room 0
    this.visited = new Set([0]); // Mark starting room as visited
  }

  /**
   * Get the current room ID
   * @returns Current room number
   */
  getCurrentRoom(): number {
    return this.currentRoom;
  }

  /**
   * Get the set of visited room IDs
   * Returns a copy to prevent external mutation
   * 
   * @returns Set of visited room IDs
   */
  getVisitedRooms(): Set<number> {
    return new Set(this.visited);
  }

  /**
   * Get available exits from the current room
   * Only returns unvisited rooms (visited rooms are "locked")
   * 
   * @returns Array of room IDs that can be moved to
   */
  getAvailableExits(): number[] {
    const edges = this.graph.getEdgesForRoom(this.currentRoom);
    return edges.filter(roomId => !this.visited.has(roomId));
  }

  /**
   * Get all exits from current room (including locked ones)
   * Useful for UI display to show which doors are locked
   * 
   * @returns Array of all connected room IDs
   */
  getAllExits(): number[] {
    return this.graph.getEdgesForRoom(this.currentRoom);
  }

  /**
   * Check if a move to a specific room is valid
   * 
   * A move is valid if:
   * 1. The rooms are connected (edge exists)
   * 2. The destination room hasn't been visited (door not locked)
   * 
   * @param roomId - Destination room ID
   * @returns True if move is valid
   */
  canMove(roomId: number): boolean {
    const edges = this.graph.getEdgesForRoom(this.currentRoom);
    return edges.includes(roomId) && !this.visited.has(roomId);
  }

  /**
   * Attempt to move to a specified room
   * 
   * If the move is valid:
   * - Updates current room
   * - Marks room as visited
   * - Records move in PDA
   * - Returns true
   * 
   * If invalid, returns false without changing state
   * 
   * @param roomId - Destination room ID
   * @returns True if move was successful
   */
  move(roomId: number): boolean {
    if (!this.canMove(roomId)) {
      return false;
    }

    // Record the move in the PDA
    this.pda.transition(`move-${this.currentRoom}->${roomId}`);

    // Update game state
    this.currentRoom = roomId;
    this.visited.add(roomId);

    return true;
  }

  /**
   * Determine the current game status
   * 
   * Win condition: All rooms visited
   * Loss condition: No available moves and not all rooms visited
   * Playing: Game is still in progress
   * 
   * @returns Current game status
   */
  getGameStatus(): GameStatus {
    const totalRooms = this.graph.getNumRooms();

    // Check win condition
    if (this.visited.size === totalRooms) {
      return 'won';
    }

    // Check loss condition
    if (this.getAvailableExits().length === 0) {
      return 'lost';
    }

    // Game is still in progress
    return 'playing';
  }

  /**
   * Get the PDA stack for display
   * Shows the history of moves made
   * 
   * @returns Array of move descriptions
   */
  getPDAStack(): string[] {
    return this.pda.getStack();
  }

  /**
   * Get the underlying graph object
   * Useful for advanced features like visualization
   * 
   * @returns The Graph instance
   */
  getGraph(): Graph {
    return this.graph;
  }

  /**
   * Get the underlying PDA object
   * @returns The PDA instance
   */
  getPDA(): PDA {
    return this.pda;
  }

  /**
   * Get game progress statistics
   * @returns Object with progress metrics
   */
  getProgress(): {
    roomsVisited: number;
    totalRooms: number;
    percentComplete: number;
    movesRemaining: number;
  } {
    const totalRooms = this.graph.getNumRooms();
    const roomsVisited = this.visited.size;

    return {
      roomsVisited,
      totalRooms,
      percentComplete: (roomsVisited / totalRooms) * 100,
      movesRemaining: this.getAvailableExits().length,
    };
  }

  /**
   * Check if a room has been visited
   * @param roomId - Room ID to check
   * @returns True if room has been visited
   */
  isRoomVisited(roomId: number): boolean {
    return this.visited.has(roomId);
  }

  /**
   * Get the solution path (for hints or debugging)
   * This reveals the guaranteed Hamiltonian path
   * 
   * @returns Array of room IDs in solution order
   */
  getSolutionPath(): number[] {
    return this.graph.getSolutionPath();
  }

  /**
   * Check if the player is following the solution path
   * Useful for difficulty analysis or hints
   * 
   * @returns True if current path matches solution so far
   */
  isFollowingSolution(): boolean {
    const solution = this.graph.getSolutionPath();
    const visitedArray = Array.from(this.visited);

    // Check if visited rooms match the beginning of solution path
    for (let i = 0; i < visitedArray.length; i++) {
      if (visitedArray[i] !== solution[i]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get detailed game state for serialization or debugging
   * @returns Complete game state object
   */
  getGameState() {
    return {
      currentRoom: this.currentRoom,
      visited: Array.from(this.visited),
      pdaStack: this.getPDAStack(),
      status: this.getGameStatus(),
      progress: this.getProgress(),
    };
  }
}